import React from 'react'
import { API_BASE } from './config'
const AuthContext = React.createContext(null)

const IDLE_TIMEOUT_MS = 10 * 60 * 1000 // 10 minutes idle → auto logout (web only)
const IDLE_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click']

let _Cap = null
try { _Cap = require('@capacitor/core').Capacitor } catch (e) {}
const isNative = () => !!(_Cap && _Cap.isNativePlatform && _Cap.isNativePlatform())
// Home-screen PWA (Add to Home Screen) — treat like an app: no idle auto-logout.
const isStandalone = () => { try { return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true } catch { return false } }

function parseJwt(token){
  try{
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1].replace(/-/g,'+').replace(/_/g,'/')))
    return payload
  }catch(e){ return null }
}

export function AuthProvider({children}){
  const [user, setUser] = React.useState(() => {
    try{
      const raw = localStorage.getItem('auth_user')
      return raw ? JSON.parse(raw) : null
    }catch(e){
      return null
    }
  })

  const refreshTimerRef = React.useRef(null)
  const idleTimerRef   = React.useRef(null)
  // Always points to latest logout so idle timer never calls a stale closure
  const logoutRef = React.useRef(null)

  const scheduleRefresh = (token, refreshToken) => {
    clearTimeout(refreshTimerRef.current)
    const payload = parseJwt(token)
    if (!payload || !payload.exp) return
    const expiresAt = payload.exp * 1000
    const now = Date.now()
    const msBefore = Math.max(1000, expiresAt - now - 60*1000) // refresh 60s before expiry
    refreshTimerRef.current = setTimeout(() => {
      _refreshToken(refreshToken).catch(err => {
        // Only log out if the refresh token is genuinely rejected. A server restart
        // (redeploy → brief 502) or network blip is transient — keep the session and
        // try again in 60s so the user isn't kicked out (and reminders keep flowing).
        if (err && err.fatal) { logoutRef.current && logoutRef.current() }
        else { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = setTimeout(() => scheduleRefresh(token, refreshToken), 60 * 1000) }
      })
    }, msBefore)
  }

  const _refreshToken = async (refreshToken, attempt = 0) => {
    let res
    try {
      res = await fetch(API_BASE + '/api/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refreshToken }) })
    } catch (netErr) {
      // Server unreachable (offline / mid-redeploy) → retry with backoff, don't log out
      if (attempt < 4) { await new Promise(r => setTimeout(r, 3000 * (attempt + 1))); return _refreshToken(refreshToken, attempt + 1) }
      const e = new Error('network'); e.transient = true; throw e
    }
    if (res.status === 401 || res.status === 403) { const e = new Error('revoked'); e.fatal = true; throw e }
    if (!res.ok) {
      // 5xx (redeploy) → transient, retry a few times before giving up
      if (attempt < 4) { await new Promise(r => setTimeout(r, 3000 * (attempt + 1))); return _refreshToken(refreshToken, attempt + 1) }
      const e = new Error('server ' + res.status); e.transient = true; throw e
    }
    const data = await res.json()
    // Functional setState — always spreads current user, never a stale closure value
    setUser(prev => {
      const next = { ...prev, token: data.token }
      try{ localStorage.setItem('auth_user', JSON.stringify(next)) }catch(e){}
      return next
    })
    scheduleRefresh(data.token, refreshToken)
    return data.token
  }

  const login = (role, name, email, token, refreshToken) => {
    const u = { role, name: name || null, email: email || null, token: token || null, refreshToken: refreshToken || null, loggedAt: Date.now() }
    setUser(u)
    try{ localStorage.setItem('auth_user', JSON.stringify(u)) }catch(e){}
    try{ window.dispatchEvent(new Event('auth-changed')) }catch(e){}
    if (token && refreshToken && role !== 'admin') scheduleRefresh(token, refreshToken)
  }

  const updateUser = (patch) => {
    setUser(prev => {
      const next = { ...prev, ...patch }
      try { localStorage.setItem('auth_user', JSON.stringify(next)) } catch (e) {}
      return next
    })
  }

  const logout = React.useCallback(() => {
    const currentUser = JSON.parse(localStorage.getItem('auth_user') || 'null')
    ;(async () => {
      try{
        const rt = currentUser?.refreshToken
        if (rt) await fetch(API_BASE + '/api/revoke', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refreshToken: rt }) })
      }catch(e){ console.error('Could not revoke token', e) }
      setUser(null)
      try{ localStorage.removeItem('auth_user') }catch(e){}
      try{ localStorage.removeItem('revive_has_consulted') }catch(e){}
      try{ window.dispatchEvent(new Event('auth-changed')) }catch(e){}
      // Only touch the native StepCounter on a real device — on web/browser the
      // plugin isn't implemented and its promise rejects (unhandled). Gate + catch.
      try{ if (isNative()) { const { StepCounter } = require('./native/stepCounter'); const r = StepCounter && StepCounter.clearAuthContext && StepCounter.clearAuthContext(); if (r && r.catch) r.catch(()=>{}) } }catch(e){}
      clearTimeout(refreshTimerRef.current)
      clearTimeout(idleTimerRef.current)
    })()
  }, [])

  // Keep logoutRef current so timers always call the latest version
  logoutRef.current = logout

  // ── Idle timeout: reset on any user activity ──────────────────────────────
  const resetIdle = React.useCallback(() => {
    clearTimeout(idleTimerRef.current)
    idleTimerRef.current = setTimeout(() => {
      if (logoutRef.current) logoutRef.current()
    }, IDLE_TIMEOUT_MS)
  }, [])

  React.useEffect(() => {
    if (!user?.token || isNative() || isStandalone()) {
      clearTimeout(idleTimerRef.current)
      return
    }
    resetIdle()
    IDLE_EVENTS.forEach(e => window.addEventListener(e, resetIdle, { passive: true }))
    return () => {
      IDLE_EVENTS.forEach(e => window.removeEventListener(e, resetIdle))
      clearTimeout(idleTimerRef.current)
    }
  }, [user?.token, resetIdle])

  // Hand the session to the native background service so it can poll for
  // notifications (doctor reviews) and post phone notifications when app is closed.
  React.useEffect(() => {
    try {
      if (isNative() && user?.refreshToken && user?.role !== 'admin' && user?.role !== 'doctor') {
        const { StepCounter } = require('./native/stepCounter')
        if (StepCounter) {
          StepCounter.requestNotifPermission && StepCounter.requestNotifPermission()
          StepCounter.setAuthContext && StepCounter.setAuthContext({ refreshToken: user.refreshToken, baseUrl: API_BASE })
        }
        // Register this phone for FCM push reminders (no-op until the plugin+Firebase exist)
        try { require('./native/pushNotifications').registerPush(user.token) } catch (e) {}
      }
    } catch (e) {}
  }, [user?.refreshToken, user?.role])

  const fetchWithAuth = async (input, init = {}) => {
    if (!user?.token) throw new Error('Not authenticated')
    const headers = { ...(init.headers || {}), Authorization: `Bearer ${user.token}` }
    let res = await fetch(input, { ...init, headers })
    if (res.status === 401 && user?.refreshToken && user?.role !== 'admin'){
      try{
        const newToken = await _refreshToken(user.refreshToken)
        const headers2 = { ...(init.headers || {}), Authorization: `Bearer ${newToken}` }
        res = await fetch(input, { ...init, headers: headers2 })
      }catch(e){
        // Only force logout if the token was genuinely revoked — not on a transient
        // server/network hiccup (which would otherwise kick the user out mid-redeploy).
        if (e && e.fatal) logout()
        throw e
      }
    }
    // Re-wrap the response: read the body ourselves before any extension can
    // consume it, then hand back a brand-new Response the caller can always read.
    try {
      const bodyText = await res.text()
      return new Response(bodyText, { status: res.status, statusText: res.statusText, headers: res.headers })
    } catch(_) { return res }
  }

  return (
    <AuthContext.Provider value={{user, login, logout, fetchWithAuth, updateUser}}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(){
  return React.useContext(AuthContext)
}

export default AuthContext
